import { useMemo } from "react";
import { useConfig } from "@/context/ConfigContext";
import { CoinIcon } from "@/components/CoinIcon";
import { CHAIN_LABELS } from "@/lib/format";
import { ShieldCheck } from "lucide-react";

// Reassurance strip: shows every coin + network accepted, so people paying
// from a centralized exchange know their asset/chain is supported.
export default function AcceptedCoins() {
  const config = useConfig();

  const { coins, networks } = useMemo(() => {
    const assets = config?.supported_payment_assets
      || ["ETH", "SOL", "BNB", "USDC", "USDT", "BTC", "DOGE", "TRX"];
    const supportedChains = config?.supported_payment_chains
      || [...(config?.chains || []), ...(config?.external_chains || [])];
    return {
      coins: assets.map((asset) => ({ key: asset.toLowerCase() })),
      networks: [...new Set(supportedChains)],
    };
  }, [config]);

  if (!config || coins.length === 0) return null;

  return (
    <div
      data-testid="accepted-coins"
      className="mt-7 overflow-hidden rounded-2xl border border-white/10 bg-white/[0.02] px-5 py-4"
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2.5">
          <ShieldCheck className="h-5 w-5 text-gold" strokeWidth={1.75} />
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Supported payments</div>
            <div className="text-sm font-bold text-white">Wallet and exchange routes</div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2" data-testid="accepted-coins-list">
          {coins.map((c) => (
            <span
              key={c.key} data-testid={`accepted-coin-${c.key}`}
              className="flex items-center gap-1.5 rounded-full border border-white/10 bg-black px-2.5 py-1 text-[11px] font-bold uppercase text-zinc-200"
            >
              <CoinIcon symbol={c.key} size={16} /> {c.key}
            </span>
          ))}
        </div>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-white/5 pt-3 text-[11px] text-zinc-500">
        <span className="uppercase tracking-[0.15em] text-zinc-600">Networks</span>
        {networks.map((n) => (
          <span key={n} data-testid={`accepted-net-${n}`} className="text-zinc-400">{CHAIN_LABELS[n] || n}</span>
        ))}
      </div>
    </div>
  );
}
